import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  OnDestroy,
  Input,
} from '@angular/core';

import { SnTag, SnCamera } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { snListStagger, snFadeIn, snFadeOut, snDetailExpand } from '@lib/common/animations';
import { SnCollection } from '@lib/common/models';
import { SnTableBaseComponent } from '@lib/core';

@Component({
  selector: 'sn-camera-table',
  templateUrl: './camera-table.component.html',
  styleUrls: ['./camera-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [snListStagger, snFadeIn, snFadeOut, snDetailExpand],
})
export class CameraTableComponent extends SnTableBaseComponent<SnCamera> implements OnDestroy {
  @Output()
  public readonly tagSelected = new EventEmitter<SnTag>();

  @Output()
  public readonly changeInTags = new EventEmitter<SnCamera>();

  @Input() public readonly canClip;
  @Input() public readonly canAdd;
  @Input() public readonly canEdit;
  @Input() public readonly canShare;
  @Input() public readonly canDelete;

  @Output() public readonly clone = new EventEmitter<SnCamera>();
  @Output() public readonly edit = new EventEmitter<SnCamera>();
  @Output() public readonly starred = new EventEmitter<SnCamera>();
  @Output() public readonly clip = new EventEmitter<SnCamera>();
  @Output() public readonly share = new EventEmitter<SnCamera>();
  @Output() public readonly shareInChat = new EventEmitter<SnCamera>();
  @Output() public readonly delete = new EventEmitter<SnCamera>();
  @Output() public readonly add = new EventEmitter<SnCamera>();
  @Output() public readonly selectedCam = new EventEmitter<SnCamera>();

  @Input()
  public selection: SelectionModel<SnCamera>;

  @Input()
  public isExpandable: boolean = true;

  public cameraIcon: string;

  private readonly _expanded = [];

  private readonly _destroy$ = new Subject();

  constructor(private readonly _camerasAPI: SnCamerasAPI) {
    super();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackBy(index: number, item: SnCamera): any {
    return item.id;
  }

  public getChangeFromListOfTag(event: SnCollection<SnTag>, camera: SnCamera): void {
    camera.tags = event;
  }

  public onTagSelected(tag: SnTag): void {
    this.tagSelected.emit(tag);
  }

  public toggleExpanded(item: SnCamera): void {
    if (!this.isExpandable) {
      this.selectedCam.next(item);
      return;
    }
    if (this.isExpanded(item.id)) {
      this._expanded.splice(this._expanded.indexOf(item.id), 1);
    } else {
      this._expanded.push(item.id);
    }
  }

  public isExpanded(id: string): boolean {
    return this._expanded.indexOf(id) > -1;
  }

  public popover(action: string, p: any, item: any): void {
    if (item.groups?.length && action === 'open') {
      p.open();
    } else if (item.groups?.length && action === 'close') {
      p.close();
    }
  }

  public toggleStarred(camera: SnCamera): void {
    this._camerasAPI
      .starredCamera(camera.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.starred.next(camera);
      });
  }

  public toggleNotStarred(camera: SnCamera): void {
    this._camerasAPI
      .notStarredCamera(camera.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.starred.next(camera);
      });
  }

  public masterToggle(): void {
    this.isAllSelected() ? this.selection.clear() : this.selection.select(...this.items);
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.items.length;

    return numSelected === numRows;
  }

  public getCameraIcon(make: string): any {
    switch (true) {
      case make?.toString()?.toLowerCase()?.includes('flock'):
        return { makeIcon: 'Icon_FS', hasIcon: true };
      case make?.toString()?.toLowerCase()?.includes('genetec'):
        return { makeIcon: 'Icon_Genetec', hasIcon: true };
      case make?.toString()?.toLowerCase()?.includes('vigilant'):
        return { makeIcon: 'Icon_Vigilant', hasIcon: true };
      case make?.toString()?.toLowerCase()?.includes('riverland'):
        return { makeIcon: 'Icon_RT', hasIcon: true };
      default:
        return { makeIcon: make, hasIcon: false };
    }
  }
}
